
import React, { useState, useMemo, useEffect } from 'react';
import {
    Box, Drawer, Button, List, ListItem, ListItemAvatar, ListItemText,
    Avatar, Typography, IconButton, Badge, Tooltip,
    Dialog, DialogTitle, DialogContent, Switch, TextField
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PeopleIcon from '@mui/icons-material/People';
import RefreshIcon from '@mui/icons-material/Refresh';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PushPinIcon from '@mui/icons-material/PushPin';
import ArchiveIcon from '@mui/icons-material/Archive';
import UnarchiveIcon from '@mui/icons-material/Unarchive';
import { Menu, MenuItem, ListItemSecondaryAction } from '@mui/material';
import StaffMiniWindow from './StaffMiniWindow';
import { useLineChat } from '../hooks/useLineChat';

const StaffChatManager: React.FC = () => {
    const { users, fetchUsers, linkReservation, unlinkReservation, togglePin, toggleArchive, loading, activeStaffChats, openStaffChat, closeStaffChat } = useLineChat();
    const [isSidebarOpen, setIsSidebarOpen] = useState<boolean>(false);
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const [selectedUser, setSelectedUser] = useState<any>(null);
    const [selectedStaffId, setSelectedStaffId] = useState<string | null>(null);
    const [showArchivedStaff, setShowArchivedStaff] = useState<boolean>(false);

    // Fetch users on mount to ensure list is up to date
    useEffect(() => {
        fetchUsers();
    }, [fetchUsers]);

    // Filter staff users (those with "STAFF:" in reservation_ids), sort pinned first
    const staffUsers = useMemo(() => {
        if (!Array.isArray(users)) return [];
        return users
            .filter((user: any) => {
                const rids = user.reservation_ids;
                if (!rids) return false;
                const idArray = Array.isArray(rids) ? rids : String(rids).split(',').map((s: string) => s.trim());
                return idArray.some((rid: any) => String(rid).startsWith('STAFF:'));
            })
            .sort((a: any, b: any) => {
                // Pinned first
                if (a.is_pinned && !b.is_pinned) return -1;
                if (!a.is_pinned && b.is_pinned) return 1;
                return 0;
            });
    }, [users]);

    // スタッフ全体の未読数
    const totalStaffUnread = useMemo(() => {
        return staffUsers.reduce((sum: number, u: any) => sum + (u.unread_count || 0), 0);
    }, [staffUsers]);

    // Split into active and archived
    const activeStaff = useMemo(() => staffUsers.filter((u: any) => !u.is_archived), [staffUsers]);
    const archivedStaff = useMemo(() => staffUsers.filter((u: any) => u.is_archived), [staffUsers]);

    const handleToggleChat = (user: any) => {
        openStaffChat(user);
        setIsSidebarOpen(false);
    };

    const handleCloseChat = (lineUserId: string) => {
        closeStaffChat(lineUserId);
    };

    const handleMenuOpen = (event: any, user: any, staffId: string) => {
        event.stopPropagation();
        setAnchorEl(event.currentTarget);
        setSelectedUser(user);
        setSelectedStaffId(staffId);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
        setSelectedUser(null);
        setSelectedStaffId(null);
    };

    const [showRenameDialog, setShowRenameDialog] = useState<boolean>(false);
    const [renameTarget, setRenameTarget] = useState<any>(null);
    const [renameValue, setRenameValue] = useState<string>('');

    const handleUpdateName = () => {
        if (!selectedUser || !selectedStaffId) return;
        const currentName = selectedStaffId.replace('STAFF:', '');
        setRenameTarget({ user: selectedUser, staffId: selectedStaffId, currentName });
        setRenameValue(currentName);
        handleMenuClose();
        setShowRenameDialog(true);
    };

    const confirmRename = async () => {
        if (!renameTarget || !renameValue.trim() || renameValue.trim() === renameTarget.currentName) {
            setShowRenameDialog(false);
            return;
        }
        setShowRenameDialog(false);
        const successUnlink = await unlinkReservation(renameTarget.user.line_user_id, renameTarget.staffId);
        if (successUnlink) {
            await linkReservation(renameTarget.user.line_user_id, `STAFF:${renameValue.trim()}`);
            fetchUsers();
        }
        setRenameTarget(null);
    };

    const [showUnregisterConfirm, setShowUnregisterConfirm] = useState<boolean>(false);
    const [unregisterTarget, setUnregisterTarget] = useState<any>(null);

    const handleUnregister = () => {
        if (!selectedUser || !selectedStaffId) return;
        setUnregisterTarget({ user: selectedUser, staffId: selectedStaffId });
        handleMenuClose();
        setShowUnregisterConfirm(true);
    };

    const confirmUnregister = async () => {
        if (!unregisterTarget) return;
        setShowUnregisterConfirm(false);
        await unlinkReservation(unregisterTarget.user.line_user_id, unregisterTarget.staffId);
        fetchUsers();
        setUnregisterTarget(null);
    };

    const handleTogglePin = async () => {
        if (!selectedUser) return;
        const newVal = !selectedUser.is_pinned;
        handleMenuClose();
        await togglePin(selectedUser.line_user_id, newVal);
    };

    const handleToggleArchive = async () => {
        if (!selectedUser) return;
        const newVal = !selectedUser.is_archived;
        handleMenuClose();
        if (newVal && selectedUser.is_pinned) {
            await togglePin(selectedUser.line_user_id, false);
        }
        await toggleArchive(selectedUser.line_user_id, newVal);
        if (newVal) {
            fetchUsers('', true);
        }
    };

    const renderStaffItem = (user: any) => {
        const rids = user.reservation_ids;
        const idArray = Array.isArray(rids) ? rids : String(rids).split(',').map((s: string) => s.trim());
        const staffId = idArray.find((id: any) => String(id).startsWith('STAFF:'));
        const staffName = staffId?.replace('STAFF:', '') || user.line_display_name;

        return (
            <ListItem
                key={user.line_user_id}
                {...{button: true} as any}
                onClick={() => handleToggleChat(user)}
                sx={{
                    m: 1,
                    width: 'auto',
                    borderRadius: '12px',
                    '&:hover': { background: '#fff' },
                    position: 'relative',
                    pr: 7,
                    cursor: 'pointer',
                    opacity: user.is_archived ? 0.6 : 1,
                    background: user.is_pinned ? '#fffbeb' : 'transparent',
                    border: user.is_pinned ? '1px solid #fcd34d' : '1px solid transparent',
                }}
            >
                <ListItemAvatar>
                    <Badge
                        overlap="circular"
                        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                        badgeContent={user.unread_count > 0 ? user.unread_count : null}
                        color="error"
                        sx={{
                            '& .MuiBadge-badge': {
                                fontSize: '10px',
                                fontWeight: 800,
                                minWidth: 18,
                                height: 18,
                            }
                        }}
                    >
                        <Avatar src={user.line_picture_url} sx={{ width: 44, height: 44 }} />
                    </Badge>
                </ListItemAvatar>
                <Tooltip title={user.is_pinned ? 'ピン留め解除' : 'ピン留めする'}>
                    <IconButton
                        size="small"
                        onClick={(e: any) => {
                            e.stopPropagation();
                            togglePin(user.line_user_id, !user.is_pinned);
                        }}
                        sx={{ color: user.is_pinned ? '#f97316' : '#cbd5e1', '&:hover': { color: '#f97316' }, mr: 0.5 }}
                    >
                        <PushPinIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                </Tooltip>
                <ListItemText
                    primary={staffName}
                    secondary={
                        <Box component="span" display="flex" alignItems="center" gap={0.5}>
                            {user.line_display_name}
                            {user.is_archived && (
                                <Typography component="span" variant="caption" sx={{ color: '#94a3b8', fontWeight: 700 }}>
                                    (archived)
                                </Typography>
                            )}
                        </Box>
                    }
                    primaryTypographyProps={{ fontWeight: 700, color: '#1e293b' }}
                    secondaryTypographyProps={{ fontSize: '12px' }}
                />
                <ListItemSecondaryAction>
                    <Box display="flex" flexDirection="column" alignItems="center" gap={0.3}>
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={(e: any) => handleMenuOpen(e, user, staffId)}
                            sx={{
                                fontSize: '11px',
                                py: 0.2,
                                minWidth: 'auto',
                                borderRadius: '6px',
                                textTransform: 'none'
                            }}
                        >
                            詳細
                        </Button>
                        <Tooltip title={user.is_archived ? 'アーカイブ解除' : 'アーカイブする'}>
                            <IconButton
                                size="small"
                                onClick={async (e: any) => {
                                    e.stopPropagation();
                                    if (!user.is_archived && user.is_pinned) {
                                        await togglePin(user.line_user_id, false);
                                    }
                                    toggleArchive(user.line_user_id, !user.is_archived);
                                }}
                                sx={{
                                    color: user.is_archived ? '#22c55e' : '#94a3b8',
                                    '&:hover': { color: user.is_archived ? '#16a34a' : '#f59e0b' },
                                    p: 0.3
                                }}
                            >
                                {user.is_archived
                                    ? <UnarchiveIcon sx={{ fontSize: 16 }} />
                                    : <ArchiveIcon sx={{ fontSize: 16 }} />
                                }
                            </IconButton>
                        </Tooltip>
                    </Box>
                </ListItemSecondaryAction>
            </ListItem>
        );
    };

    return (
        <>
            {/* Rotated Toggle Button */}
            <Box
            sx={{
                position: 'fixed',
                right: 0,
                top: '235px',
                transform: 'translateX(-10%) translateY(-50%) rotate(-90deg)',
                transformOrigin: 'right center',
                zIndex: 1100,
            }}
        >
                <Button
                    variant="contained"
                    color="primary"
                    startIcon={<PeopleIcon sx={{ fontSize: { xs: 14, md: 18 } }} />}
                    onClick={() => {
                        setIsSidebarOpen(true);
                        fetchUsers('', showArchivedStaff);
                    }}
                    sx={{
                        borderRadius: '8px 8px 0 0',
                        padding: { xs: '6px 12px', md: '10px 20px' },
                        fontWeight: 800,
                        fontSize: { xs: '10px', md: '14px' },
                        boxShadow: '0 -4px 12px rgba(0,0,0,0.15)',
                        background: 'linear-gradient(135deg, #4f46e5 0%, #3b82f6 100%)',
                        textTransform: 'none',
                        position: 'relative',
                        '&:hover': {
                            background: 'linear-gradient(135deg, #4338ca 0%, #2563eb 100%)',
                        }
                    }}
                >
                    STAFF
                    {totalStaffUnread > 0 && (
                        <Box
                            sx={{
                                position: 'absolute',
                                top: -5,
                                right: -5,
                                width: 12,
                                height: 12,
                                borderRadius: '50%',
                                background: '#ef4444',
                                border: '2px solid white',
                                boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
                            }}
                        />
                    )}
                </Button>
            </Box>

            {/* Staff Sidebar */}
            <Drawer
                anchor="right"
                open={isSidebarOpen}
                onClose={() => setIsSidebarOpen(false)}
                sx={{ zIndex: 11000 }}
                PaperProps={{
                    sx: { width: 320, background: '#f8fafc' }
                }}
            >
                <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e2e8f0' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="h6" fontWeight={800} color="#1e293b">スタッフ一覧</Typography>
                        <IconButton onClick={() => fetchUsers('', showArchivedStaff)} size="small" disabled={loading}>
                            <RefreshIcon sx={{ fontSize: 20, animation: loading ? 'spin 1s linear infinite' : 'none' }} />
                        </IconButton>
                    </Box>
                    <IconButton onClick={() => setIsSidebarOpen(false)} size="small">
                        <CloseIcon />
                    </IconButton>
                </Box>
                <style>{`
                    @keyframes spin {
                        from { transform: rotate(0deg); }
                        to { transform: rotate(360deg); }
                    }
                `}</style>
                <List sx={{ pt: 1, pb: 0 }}>
                    {activeStaff.length === 0 && !showArchivedStaff ? (
                        <Box sx={{ p: 4, textAlign: 'center', opacity: 0.6 }}>
                            <PeopleIcon sx={{ fontSize: 48, mb: 1 }} />
                            <Typography variant="body2">登録されたスタッフがいません</Typography>
                        </Box>
                    ) : (
                        activeStaff.map(renderStaffItem)
                    )}
                </List>

                {/* Archived section */}
                <Box sx={{ px: 2, py: 1, borderTop: '1px solid #e2e8f0' }}>
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                        <Box display="flex" alignItems="center" gap={0.5}>
                            <ArchiveIcon sx={{ fontSize: 16, color: '#94a3b8' }} />
                            <Typography variant="caption" fontWeight={700} color="#94a3b8">
                                アーカイブ ({archivedStaff.length})
                            </Typography>
                        </Box>
                        <Switch
                            size="small"
                            checked={showArchivedStaff}
                            onChange={(e: any) => {
                                setShowArchivedStaff(e.target.checked);
                                if (e.target.checked) fetchUsers('', true);
                            }}
                        />
                    </Box>
                </Box>
                {showArchivedStaff && archivedStaff.length > 0 && (
                    <List sx={{ pt: 0 }}>
                        {archivedStaff.map(renderStaffItem)}
                    </List>
                )}
            </Drawer>

            {/* Mini Window Container */}
            <Box
                sx={{
                    position: 'fixed',
                    bottom: 0,
                    right: 40,
                    display: 'flex',
                    flexDirection: 'row-reverse',
                    gap: '16px',
                    alignItems: 'flex-end',
                    zIndex: 10000,
                    pointerEvents: 'none',
                }}
            >
                {activeStaffChats.map((user: any) => (
                    <Box key={user.line_user_id} sx={{ pointerEvents: 'auto' }}>
                        <StaffMiniWindow
                            user={user}
                            onClose={() => handleCloseChat(user.line_user_id)}
                        />
                    </Box>
                ))}
            </Box>

            {/* Detail Menu */}
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                sx={{ zIndex: 12000 }}
                PaperProps={{
                    sx: { borderRadius: '12px', boxShadow: '0 4px 20px rgba(0,0,0,0.1)', minWidth: 180 }
                }}
            >
                <MenuItem onClick={handleTogglePin} sx={{ py: 1.5 }}>
                    <PushPinIcon sx={{ fontSize: 18, mr: 1.5, color: selectedUser?.is_pinned ? '#f97316' : '#94a3b8' }} />
                    <Typography variant="body2" fontWeight={600}>
                        {selectedUser?.is_pinned ? 'ピン留め解除' : 'ピン留めする'}
                    </Typography>
                </MenuItem>
                <MenuItem onClick={handleUpdateName} sx={{ py: 1.5 }}>
                    <EditIcon sx={{ fontSize: 18, mr: 1.5, color: '#6366f1' }} />
                    <Typography variant="body2" fontWeight={600}>表示名変更</Typography>
                </MenuItem>
                <MenuItem onClick={handleToggleArchive} sx={{ py: 1.5 }}>
                    {selectedUser?.is_archived
                        ? <UnarchiveIcon sx={{ fontSize: 18, mr: 1.5, color: '#22c55e' }} />
                        : <ArchiveIcon sx={{ fontSize: 18, mr: 1.5, color: '#f59e0b' }} />
                    }
                    <Typography variant="body2" fontWeight={600}>
                        {selectedUser?.is_archived ? 'アーカイブ解除' : 'アーカイブする'}
                    </Typography>
                </MenuItem>
                <MenuItem onClick={handleUnregister} sx={{ py: 1.5, color: '#ef4444' }}>
                    <DeleteIcon sx={{ fontSize: 18, mr: 1.5, color: '#ef4444' }} />
                    <Typography variant="body2" fontWeight={600}>スタッフ登録解除</Typography>
                </MenuItem>
            </Menu>

            {/* Unregister Confirm Dialog */}
            <Dialog
                open={showUnregisterConfirm}
                onClose={() => setShowUnregisterConfirm(false)}
                sx={{ zIndex: 12000 }}
                PaperProps={{ sx: { borderRadius: '16px', p: 1 } }}
            >
                <DialogTitle sx={{ fontWeight: 800, fontSize: '16px', pb: 0 }}>スタッフ登録解除</DialogTitle>
                <DialogContent sx={{ pt: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                        {unregisterTarget?.user?.line_display_name} のスタッフ登録を解除しますか？
                    </Typography>
                    <Box display="flex" justifyContent="flex-end" gap={1} mt={2}>
                        <Button onClick={() => setShowUnregisterConfirm(false)} size="small" sx={{ fontWeight: 700 }}>キャンセル</Button>
                        <Button variant="contained" color="error" size="small" sx={{ fontWeight: 700 }} onClick={confirmUnregister}>解除する</Button>
                    </Box>
                </DialogContent>
            </Dialog>

            {/* Rename Dialog */}
            <Dialog
                open={showRenameDialog}
                onClose={() => setShowRenameDialog(false)}
                sx={{ zIndex: 12000 }}
                PaperProps={{ sx: { borderRadius: '16px', p: 1 } }}
            >
                <DialogTitle sx={{ fontWeight: 800, fontSize: '16px', pb: 0 }}>表示名変更</DialogTitle>
                <DialogContent sx={{ pt: 1 }}>
                    <TextField
                        autoFocus
                        fullWidth
                        size="small"
                        label="新しいスタッフ名"
                        value={renameValue}
                        onChange={(e: any) => setRenameValue(e.target.value)}
                        onKeyDown={(e: any) => { if (e.key === 'Enter' && !e.nativeEvent.isComposing && renameValue.trim()) confirmRename(); }}
                        sx={{ mt: 1, mb: 2 }}
                    />
                    <Box display="flex" justifyContent="flex-end" gap={1}>
                        <Button onClick={() => setShowRenameDialog(false)} size="small" sx={{ fontWeight: 700 }}>キャンセル</Button>
                        <Button variant="contained" size="small" sx={{ fontWeight: 700 }} disabled={!renameValue.trim()} onClick={confirmRename}>変更</Button>
                    </Box>
                </DialogContent>
            </Dialog>
        </>
    );
};

export default StaffChatManager;
